import { IconWarning } from "@/assets/icons/geist/IconWarning";
import { EnvironmentClassItem } from "@/components/EnvironmentClassItem";
import { Link } from "@/components/flexkit/Link";
import { Label } from "@/components/podkit/forms/Label";
import { cn } from "@/components/podkit/lib/cn";
import { SelectContentCombobox, SelectTrigger } from "@/components/podkit/select/Select";
import { Text } from "@/components/podkit/typography/Text";
import {
    getCompareLabel,
    getEnvironmentClassStatus,
    sortEnvironmentClassEntries,
    type EnvironmentClassEntry,
} from "@/hooks/use-environment-class-entries";
import type { PlainRunnerEnvironmentClass } from "@/queries/environment-queries";
import { ComboboxProvider } from "@ariakit/react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { RunnerPhase } from "gitpod-next-api/gitpod/v1/runner_pb";
import { Check } from "lucide-react";
import { memo, startTransition, useId, useMemo, useState, type FC, type ReactNode } from "react";
import { ComboboxItem } from "@ariakit/react";

function selectionFromValue(
    environmentClassEntries: EnvironmentClassEntry[],
    value: string,
): EnvironmentClassEntry | undefined {
    const [runnerId, classId] = value.split(":");
    return environmentClassEntries.find((v) => v?.clazz?.id == classId && v?.clazz.runnerId == runnerId);
}

function valueFromSelection(selection: PlainRunnerEnvironmentClass): string {
    return `${selection.runnerId || "unknown"}:${selection?.id || "unknown"}`;
}

type Props = {
    environmentClassEntries: EnvironmentClassEntry[];
    label?: string;
    value?: EnvironmentClassEntry;
    loading: boolean;
    disabled: boolean;
    name: string;
    onChange: (value: EnvironmentClassEntry | undefined) => void;
    placeholder?: ReactNode;
    sortEntries?: boolean;
};

export const EnvironmentClassSelect: FC<Props> = memo(
    ({ environmentClassEntries, label, value, loading, disabled, name, onChange, placeholder, sortEntries = true }) => {
        const elId = useId();

        const [open, setOpen] = useState(false);
        const [searchValue, setSearchValue] = useState("");

        const hasAvailableClasses = environmentClassEntries.length > 0;
        const isDegraded = value?.runner?.status?.phase === RunnerPhase.DEGRADED;

        const matches = useMemo(() => {
            if (!searchValue) {
                return sortEntries ? sortEnvironmentClassEntries(environmentClassEntries) : environmentClassEntries;
            }

            const words = searchValue.split(" ");
            const matches = environmentClassEntries.filter((v) => {
                const label = getCompareLabel(v);
                return words.reduce((acc, word) => acc && label.includes(word), true);
            });

            // Radix Select does not work if we don't render the selected item, so we
            // make sure to include it in the list of matches.
            const vv = (value && valueFromSelection(value.clazz)) || "";
            if (value !== undefined) {
                const selectedEnvironmentClass = environmentClassEntries.find((v) => valueFromSelection(v.clazz) == vv);
                if (selectedEnvironmentClass && !matches.includes(selectedEnvironmentClass)) {
                    matches.push(selectedEnvironmentClass);
                }
            }
            return sortEnvironmentClassEntries(matches);
        }, [environmentClassEntries, value, searchValue, sortEntries]);

        const defaultPlaceholder = useMemo(() => {
            return (
                <div className="flex h-14 w-11/12 items-center p-6">
                    {hasAvailableClasses ? "Select an environment class" : "No available environment classes"}
                </div>
            );
        }, [hasAvailableClasses]);

        return (
            <div className="flex items-center space-x-2">
                <div className="flex w-full flex-col gap-2">
                    {label && (
                        <Label htmlFor={elId} className="font-medium">
                            {label}
                        </Label>
                    )}
                    <SelectPrimitive.Root
                        value={value ? valueFromSelection(value.clazz) : undefined}
                        onValueChange={(s) => onChange(selectionFromValue(environmentClassEntries, s))}
                        open={open}
                        name={name}
                        onOpenChange={setOpen}
                        disabled={loading || disabled || !hasAvailableClasses}
                    >
                        <ComboboxProvider
                            open={open}
                            setOpen={setOpen}
                            resetValueOnHide
                            includesBaseElement={false}
                            setValue={(value) => {
                                startTransition(() => {
                                    setSearchValue(value.toLowerCase());
                                });
                            }}
                        >
                            <SelectTrigger
                                id={elId}
                                data-testid="environment-class-select-trigger"
                                aria-label="Environment class"
                                loading={loading}
                                className="flex flex-row items-center justify-center p-0 pr-4"
                            >
                                {value ? (
                                    <EnvironmentClassItem
                                        id={valueFromSelection(value.clazz)}
                                        environmentClassEntry={value}
                                        className="pointer-events-none"
                                    />
                                ) : (
                                    placeholder || defaultPlaceholder
                                )}
                            </SelectTrigger>

                            {hasAvailableClasses && (
                                <SelectContentCombobox inputPlaceholder="Search environment classes">
                                    {matches.map((environmentClassEntry) => {
                                        const { policyRestricted, offline } =
                                            getEnvironmentClassStatus(environmentClassEntry);
                                        const id = valueFromSelection(environmentClassEntry.clazz);
                                        const isSelected = !!value && id == valueFromSelection(value.clazz);
                                        return (
                                            <SelectPrimitive.Item
                                                key={id}
                                                value={id}
                                                className={cn(
                                                    "relative flex w-full cursor-default select-none items-center",
                                                    "rounded-sm text-sm outline-none",
                                                    "hover:cursor-pointer",
                                                    "focus:text-accent-foreground focus:bg-surface-02 focus-visible:ring-0",
                                                    "data-[disabled]:pointer-events-none",
                                                    "hover:text-accent-foreground hover:bg-surface-02",
                                                    "data-[active-item]:bg-surface-02",
                                                    "focus:bg-surface-02",
                                                    "group w-full border-b border-border-light last:border-b-0",
                                                )}
                                                disabled={offline || policyRestricted}
                                                asChild
                                            >
                                                <ComboboxItem>
                                                    <SelectPrimitive.ItemText asChild>
                                                        <span className="sr-only">
                                                            <span>{environmentClassEntry.clazz.displayName}</span>
                                                            <span>{environmentClassEntry.runner.name}</span>
                                                            <span>{environmentClassEntry.clazz.description}</span>
                                                        </span>
                                                    </SelectPrimitive.ItemText>
                                                    <EnvironmentClassItem
                                                        id={id}
                                                        environmentClassEntry={environmentClassEntry}
                                                        icon={isSelected ? Check : undefined}
                                                    />
                                                </ComboboxItem>
                                            </SelectPrimitive.Item>
                                        );
                                    })}
                                </SelectContentCombobox>
                            )}
                        </ComboboxProvider>
                    </SelectPrimitive.Root>
                    {isDegraded && (
                        <>
                            <div className="flex flex-col gap-2 rounded-lg border-[0.5px] border-border-base bg-surface-yield p-4">
                                <div className="flex items-center gap-1">
                                    <IconWarning size="base" className="text-content-orange" />
                                    <Text className="text-sm font-bold">This runner is in a degraded state</Text>
                                </div>
                                <Text className="text-sm">
                                    Environments may fail to start successfully on this runner.{" "}
                                    <Link to={`/settings/runners/${value?.runner?.runnerId}`} className="underline">
                                        View runner settings for details
                                    </Link>
                                </Text>
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    },
);
EnvironmentClassSelect.displayName = "EnvironmentClassSelect";
